/**
 * React side of the relation rail (spec §8.15): Loro selectors for the configuration, styles and
 * page relations, query-track results through the graph query cache, and the one accessor that reads
 * row geometry from the virtualiser.
 */
import { useQuery } from "@tanstack/react-query";
import type { Virtualizer } from "@tanstack/react-virtual";
import type { LoroDoc } from "loro-crdt";
import { flattenBlocks } from "../blocks/model";
import { useEffect, useMemo, useState } from "react";
import { api, ApiError, unwrap } from "../api/client";
import { queryCacheKey, runWithCache } from "../graph/queryCache";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { DEFAULT_BUNDLES, type BundleOptions, type RowGeometry } from "./layout";
import { queryEdges, readPageRelations, readRail, readStyles, type PageRelation, type QueryEdge, type RailConfig, type RailStyle } from "./model";

const sameJson = <T,>(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b);

export function useRailConfig(doc: LoroDoc | null): RailConfig {
  return useLoroSelector(doc, readRail, sameJson) ?? { enabled: false, tracks: [] };
}

export function useRailStyles(wsDoc: LoroDoc | null): Record<string, RailStyle> {
  return useLoroSelector(wsDoc, readStyles, sameJson) ?? {};
}

/** Block ids are read inside the selector: `useLoroSelector` caches by document version, so an id
 *  list passed in from a render could be stale for the version it is cached under. */
export function usePageRelations(handle: DocHandle): ReturnType<typeof readPageRelations> {
  const empty = { relations: [] as PageRelation[], groups: new Map() };
  return (
    useLoroSelector(
      handle.doc,
      (d) => readPageRelations(d, handle.crdtDocId, flattenBlocks(d).map((r) => r.treeId)),
      (a, b) => sameJson(a.relations, b.relations) && sameJson([...a.groups], [...b.groups]),
    ) ?? empty
  );
}

/**
 * Row geometry for every visible row — measured where rendered, estimated elsewhere — from the
 * virtualiser's measurement cache. Verified against @tanstack/virtual-core 3.17.9 (react-virtual
 * 3.14.11): `measurementsCache[i]` is `{index, start, size, end, key, lane}` for every index below
 * `count`, recomputed by `getTotalSize()`/`getVirtualItems()` during render. With one lane the cache
 * is a lazy Proxy over a sparse array that materialises entries on index access, so it must be read
 * by index — `Array.prototype.map` skips the holes. (`getOffsetForIndex` is a scroll target for
 * alignment, not a row start.) The one place the rail reads the virtualiser.
 */
export function rowGeometry(virtualizer: Virtualizer<HTMLDivElement, Element>): RowGeometry[] {
  virtualizer.getTotalSize(); // makes sure the cache reflects the current count and measurements
  const cache = virtualizer.measurementsCache;
  const rows: RowGeometry[] = [];
  for (let i = 0; i < cache.length; i++) {
    const m = cache[i]!;
    rows.push({ start: m.start, size: m.size });
  }
  return rows;
}

export interface QueryTrackState {
  edges: QueryEdge[];
  stale: boolean;
  error: string | null;
  loading: boolean;
}

/**
 * Query-track results shared by every hook instance (the rail and the relations menu), keyed by
 * document, track and query text, so opening the menu shows a track's state without a second request.
 */
const trackResults = new Map<string, QueryTrackState>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();
const trackKey = (docId: string, trackId: string, sparql: string) => JSON.stringify([docId, trackId, sparql]);

function setTrackResult(key: string, state: QueryTrackState) {
  trackResults.set(key, state);
  listeners.forEach((l) => l());
}

async function runTrack(workspaceId: string, docId: string, trackId: string, sparql: string) {
  const key = trackKey(docId, trackId, sparql);
  if (inFlight.has(key)) return;
  inFlight.add(key);
  setTrackResult(key, { edges: trackResults.get(key)?.edges ?? [], stale: false, error: null, loading: true });
  try {
    const run = await runWithCache(workspaceId, sparql, await queryCacheKey("rail", docId, trackId, sparql));
    const edges = queryEdges((run.result.results.results?.bindings ?? []) as Array<Record<string, { value: string } | undefined>>);
    setTrackResult(key, { edges, stale: run.stale, error: run.error ?? null, loading: false });
  } catch (e) {
    setTrackResult(key, { edges: [], stale: false, error: errorMessage(e), loading: false });
  } finally {
    inFlight.delete(key);
  }
}

/** The server's message: an API error's body is `{"detail": …}` passed on from the graph service. */
function errorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e);
  try {
    const body = JSON.parse(e.detail) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // not JSON: the detail is the message
  }
  return e.detail;
}

/**
 * Visible query tracks' results through the cached query path; offline it replays the cache. With
 * `run` (the rail) each track is queried when the page mounts and whenever its query changes; without
 * it (the menu) the hook only reads what the rail fetched. `refresh` re-runs one track on demand.
 */
export function useQueryTracks(workspaceId: string | undefined, docId: string, config: RailConfig, run = true): { states: Map<string, QueryTrackState>; refresh(trackId: string): void } {
  const tracks = config.enabled ? config.tracks.filter((t) => t.visible && t.source.kind === "query") : [];
  const signature = JSON.stringify(tracks.map((t) => [t.id, t.source.kind === "query" ? t.source.sparql : ""]));
  const [version, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  useEffect(() => {
    if (!run || !workspaceId) return;
    for (const [trackId, sparql] of JSON.parse(signature) as Array<[string, string]>) void runTrack(workspaceId, docId, trackId, sparql);
  }, [run, workspaceId, docId, signature]);
  // a new Map only when a result or the track list changed, so the rail's memo stays stable
  const states = useMemo(() => {
    const m = new Map<string, QueryTrackState>();
    for (const [trackId, sparql] of JSON.parse(signature) as Array<[string, string]>) {
      const st = trackResults.get(trackKey(docId, trackId, sparql));
      if (st) m.set(trackId, st);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` marks a store change
  }, [version, signature, docId]);
  const refresh = (trackId: string) => {
    const t = tracks.find((x) => x.id === trackId);
    if (t?.source.kind === "query" && workspaceId) void runTrack(workspaceId, docId, t.id, t.source.sparql);
  };
  return { states, refresh };
}

/** A predicate's label from the ontology index, else its local name. */
export function usePredicateLabel(iri: string): string {
  const local = iri.split(/[#/]/).pop() || iri;
  const q = useQuery({
    queryKey: ["term-label", iri],
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async () => {
      const path = encodeURI(iri).replace(/#/g, "%23").replace(/\?/g, "%3F");
      const r = await fetch(`/api/ontology/terms/${path}`, { credentials: "same-origin" });
      if (!r.ok) return null;
      return ((await r.json()) as { label?: string }).label ?? null;
    },
  });
  return q.data || local;
}

/** The administrator's bundle settings (spec §8.15, §8.26), the same for every reader; the defaults until
 *  they arrive, and offline. */
export function useBundleOptions(): BundleOptions {
  const q = useQuery({
    queryKey: ["displaySettings"],
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<{ railBundleOverlap: BundleOptions["overlap"]; railBundleGroups: BundleOptions["groups"] }>(await api.GET("/api/display-settings")),
  });
  return useMemo(
    () => (q.data ? { overlap: q.data.railBundleOverlap, groups: q.data.railBundleGroups } : DEFAULT_BUNDLES),
    [q.data],
  );
}
